import { useReviewContext } from "@hooks/useReviewSnippet";
import { useRoutes } from "@hooks/useRoutes";
import { useShopify } from "@hooks/useShopify";
import { useSite } from "@hooks/useSite";
import { useRouteLoaderData } from "~/hooks/useRouteLoaderData";

import { type ComponentProps, type ResolvedLink } from "@ts/components";
import {
  type ElasticCollection,
  type ElasticProduct,
} from "@usereactify/search";
import type { MoneyV2, CurrencyCode } from "types/storefront.types";
import React, { FC, memo, useEffect, useMemo } from "react";

import { type ProductCard } from "types/product";

import { KlaviyoItemResolver } from "~/lib/KlaviyoItemResolver";
import { GoogleAnalyticsItemResolver } from "~/lib/GoogleAnalyticsItemResolver";

export type SearchResultsProductCardInputProps = ComponentProps & {
  pagePosition: number;
  collection: ElasticCollection;
  product: ElasticProduct;
};

export type SearchResultsProductCardOutputProps = Omit<
  SearchResultsProductCardInputProps,
  "product"
> & {
  product: ProductCard;
  link: ResolvedLink;
  productGroup: string;
};

// Function to calculate pricing
const calculatePricing = (
  product: ElasticProduct,
  currencyCode: CurrencyCode,
  salePrice: number,
  isProductOnSale: boolean,
): { price: MoneyV2; compareAtPrice: MoneyV2 | null } => {
  // Get the highest price and compare at price for the product
  const prices = product.variants?.reduce(
    (acc, variant) => {
      const currentPrices = variant.presentment_prices?.find(
        (p) => p.price?.currency_code === currencyCode,
      );

      const price = Number(currentPrices?.price?.amount) || 0;
      const comparePrice = Number(currentPrices?.compare_at_price?.amount) || 0;

      return {
        price: {
          amount: Math.max(price, acc.price.amount),
          currencyCode,
        },
        compareAtPrice: {
          amount: Math.max(comparePrice, acc.compareAtPrice.amount),
          currencyCode,
        },
      };
    },
    {
      price: { amount: 0, currencyCode },
      compareAtPrice: { amount: 0, currencyCode },
    },
  );

  // Return appropriate pricing

  // If sale is active and product has a sale price discount
  if (isProductOnSale && prices && salePrice < prices.price?.amount)
    return {
      price: { amount: salePrice.toString(), currencyCode },
      compareAtPrice: { amount: prices.price.amount.toString(), currencyCode },
    };

  // Product has regular compare at price discount
  if (prices && prices.compareAtPrice?.amount > prices.price?.amount)
    return {
      price: { amount: prices.price.amount.toString(), currencyCode },
      compareAtPrice: {
        amount: prices.compareAtPrice.amount.toString(),
        currencyCode,
      },
    };

  // Product has no discount
  if (prices && prices.price?.amount > 0)
    return {
      price: { amount: prices?.price?.amount.toString(), currencyCode },
      compareAtPrice: null,
    };

  // This should never happen.
  return {
    price: { amount: "0.00", currencyCode },
    compareAtPrice: null,
  };
};

export const withSearchResultsProductCard = (
  Component: FC<SearchResultsProductCardOutputProps>,
) => {
  return memo(
    ({
      name = "SearchResultsProductCard",
      product,
      collection,
      pagePosition,
    }: SearchResultsProductCardInputProps) => {
      const { enableSale } = useRouteLoaderData("routes/$site");
      const { addProductToQueue } = useReviewContext();
      const { getBadgeFromTags } = useShopify();
      const { linkResolver } = useRoutes();
      const site = useSite();
      const { tags } = product || [];
      const productName =
        (product as any).metafields_custom_netsuite_style_name || "";
      const productStatus =
        (product as any).metafields_custom_netsuite_status || "";
      const launchDateMetafield =
        (product as any).metafields_custom_launch_date || "";
      const salePriceMetafield =
        (product as any)[
          `metafields_custom_sale_price_${site.currencyCode?.toLowerCase()}`
        ] || "";
      const salePrice = salePriceMetafield ? Number(salePriceMetafield) : 0;
      const isProductOnSale = salePrice > 0 && enableSale;
      const productPrice = calculatePricing(
        product,
        site.currencyCode,
        salePrice,
        isProductOnSale,
      );

      const hasColourOption = product.options.some(
        (item) => item.includes("Color") || item.includes("Colour"),
      );
      const subTitle =
        hasColourOption && product.variant_options
          ? product.variant_options[0]
          : "";

      const link = {
        ...linkResolver(product),
        url: product.url,
      };
      const image = {
        src: product?.images?.[0]?.src,
        alt: product?.images?.[0]?.alt,
      };
      const hoverImage = {
        src: product?.images?.[1]?.src,
        alt: product?.images?.[1]?.alt,
      };

      const availableForSale =
        product.variants?.some((variant) => variant.available === true) ??
        false;

      const badge = getBadgeFromTags(
        tags,
        productStatus,
        availableForSale,
        launchDateMetafield,
        productPrice,
      );

      // Review group is a tag like "RGroup_Sanderlings" used to fetch reviews
      // from reviews.io relating to the product
      const reviewGroup =
        product.tags.find((tag) => tag.toLowerCase().startsWith("rgroup_")) ??
        null;

      useEffect(() => {
        addProductToQueue(product.handle);
      }, [product]);

      const klaviyoItem = useMemo(() => {
        return new KlaviyoItemResolver({
          site,
        }).resolveFromReactifyProduct({
          reactifyProduct: product,
        });
      }, [product, site]);

      const googleAnalyticsItem = useMemo(() => {
        return new GoogleAnalyticsItemResolver({
          site,
        }).resolveFromReactifyProduct({
          reactifyProduct: product,
        });
      }, [product, site]);

      const productCard = {
        title: productName ?? "",
        subtitle: subTitle,
        handle: product.handle,
        badge,
        image: image?.src,
        imageAlt: image?.alt ?? null,
        hoverImage: hoverImage?.src,
        hoverImageAlt: hoverImage?.alt ?? null,
        price: productPrice.price,
        compareAtPrice: productPrice.compareAtPrice,
        firstAvailableVariantId:
          product.variants
            ?.find((variant) => variant.available === true)
            ?.id.toString() ?? null,
        colourVariantCount: 0,
        klaviyoItem,
        googleAnalyticsItem,
      };

      Component.displayName = name;

      return useMemo(
        () => (
          <Component
            // This legacy card layout breaks when there is a space between the
            // currency code and amount. For now, we are removing spaces from
            // the formatted price, just for this component.
            // https://bared-melbourne.slack.com/archives/C045GT6BNAU/p1757028442352629?thread_ts=1757024149.407659&cid=C045GT6BNAU
            product={productCard}
            productGroup={reviewGroup}
            pagePosition={pagePosition}
          />
        ),
        [product],
      );
    },
  );
};
